/**
 * Générer un entraînement, côté parent : les matières, puis les compétences,
 * puis l'aperçu (Hedi, 27 septembre 2026).
 *
 * L'écran lui-même vit dans `ui/Generer` depuis le 5 octobre 2026, l'élève de
 * lycée composant ses propres entraînements par le même chemin. Ce qui reste
 * ici est ce qui n'appartient qu'au parent : l'enfant pris dans l'adresse, les
 * appels sous `/enfants/{id}/…`, et ce qui suit l'ajout — oublier la copie des
 * cahiers de cet enfant, puis mener aux entraînements composés.
 *
 * **Ajouter ne l'envoie pas.** Les exercices s'ajoutent à la feuille en cours,
 * comme depuis un chapitre ; c'est la clôture de la feuille qui l'envoie à
 * l'enfant, et l'écran le dit sous le bouton.
 */

import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { oublierLesOuvrages } from "@/ui/enCache";
import { Generer, type AppelsDeGeneration } from "@/ui/Generer";
import { entrainementsDe } from "@/v2/chemins";

// Réexportés : `v2/generer.test.ts` et ce qui s'en sert les cherchent ici.
export { MODES, manque, type Mode } from "@/ui/Generer";

export function GenererScreen() {
  const { childId = "" } = useParams();
  const navigate = useNavigate();

  const appels = useMemo<AppelsDeGeneration>(
    () => ({
      proposer: (corps) =>
        call("proposerUnEntrainement", { params: { child_id: childId }, body: corps }),
      tirer: (corps) => call("tirerUnEntrainement", { params: { child_id: childId }, body: corps }),
      ajouter: async (exerciseIds) => {
        await call("genererUnEntrainement", {
          params: { child_id: childId },
          body: { exercise_ids: exerciseIds },
        });
      },
    }),
    [childId],
  );

  return (
    <Generer
      pour="parent"
      appels={appels}
      apresAjout={() => {
        // Le compte des exercices déjà faits change sur le catalogue et les
        // sommaires : garder leur copie montrerait le contraire de ce qui vient
        // d'être fait.
        oublierLesOuvrages(childId);
        navigate(`${entrainementsDe(childId)}/composes`);
      }}
    />
  );
}
